import {DOCUMENT, Injectable, inject, signal} from '@angular/core';

// Type-only import — erased at build time, so it does NOT pull the mcu-backed generator
// (and thus @material/material-color-utilities) into this service's static graph. The
// value functions are loaded LAZILY inside apply() (see the class docblock).
import type {CustomColors, ThemeSeeds} from '@rg/shared-ng/theme';

/**
 * Default RealGuest brand seeds — the legacy M2 brand colors, now driving the M3 theme.
 * `primary` is the purple, `tertiary` the gold accent (M3 has no "accent" role).
 */
export const DEFAULT_BRAND_SEEDS: ThemeSeeds = {
  primary: '#6738d6',
  tertiary: '#ebb652',
};

/**
 * Extended semantic colors M3 has no built-in role for. Emitted as `--rg-<name>` +
 * on/container/on-container variants. Seeds are muted, balanced-chroma tones so the
 * generated containers read as a consistent soft set (the raw brand greens/reds were
 * too vivid/uneven). `danger` is used instead of M3's pinkish `error` for the cards.
 */
export const BRAND_CUSTOM_COLORS: CustomColors = {
  success: '#2e7d32',
  warning: '#ef6c00',
  danger: '#c62828',
};

/** localStorage key for the persisted dark-mode choice ('1' dark / '0' light). */
const DARK_MODE_KEY = 'rg-dark-mode';

/**
 * Runtime Material 3 theming. Generates the `--mat-sys-*` color tokens from the active
 * brand seeds (+ light/dark mode) and writes them onto `<html>`, so every Material
 * component re-colors live — no rebuild. `styles.scss` emits a build-time baseline of
 * the same tokens, so the app is themed before this runs; `apply()` then refines it to
 * the brand-exact colors and is the seam for per-tenant / dark-mode switching.
 *
 * Dark mode: the user's choice is persisted in localStorage and restored at boot
 * (falling back to the OS `prefers-color-scheme` on first visit). `apply()` — run from
 * an APP_INITIALIZER before first paint — also toggles the `.dark` class on `<html>`,
 * which is what Tailwind's `darkMode: 'selector'` utilities and any `.dark`-scoped SCSS
 * key off. So both the Material token layer AND the Tailwind layer flip together.
 *
 * The generator (and thus material-color-utilities) is loaded LAZILY inside `apply()`
 * — mirroring the website's ThemeService. mcu is an ESM that trips the Angular
 * unit-test builder's Node loader, so keeping it out of this service's static import
 * graph keeps every component that injects ThemeService (and their specs) mcu-free; it
 * also keeps mcu out of the eager main bundle (it loads once, during the boot
 * APP_INITIALIZER, then is cached for every later toggle).
 */
@Injectable({providedIn: 'root'})
export class ThemeService {
  private readonly document = inject(DOCUMENT);

  private readonly _seeds = signal<ThemeSeeds>(DEFAULT_BRAND_SEEDS);
  private readonly _dark = signal(this.readStoredDark());

  readonly seeds = this._seeds.asReadonly();
  readonly isDark = this._dark.asReadonly();

  /** Recompute and write the `--mat-sys-*` overrides for the current seeds/mode. */
  async apply(): Promise<void> {
    const {generateSysColors, generateCustomColorRoles} = await import('@rg/shared-ng/theme');
    const root = this.document.documentElement;
    const vars = {
      ...generateSysColors(this._seeds(), {dark: this._dark()}),
      ...generateCustomColorRoles(BRAND_CUSTOM_COLORS, {dark: this._dark()}),
    };
    for (const [name, value] of Object.entries(vars)) {
      root.style.setProperty(name, value);
    }
    // Keep native form controls / scrollbars in step with the theme.
    root.style.colorScheme = this._dark() ? 'dark' : 'light';
    // Tailwind `dark:` utilities + `.dark`-scoped SCSS gate on this class (darkMode: 'selector').
    root.classList.toggle('dark', this._dark());
  }

  /** Re-theme to a different brand (e.g. per-tenant) and apply immediately. */
  setBrand(seeds: ThemeSeeds): void {
    this._seeds.set(seeds);
    void this.apply();
  }

  setDarkMode(dark: boolean): void {
    this._dark.set(dark);
    this.persistDark(dark);
    void this.apply();
  }

  toggleDarkMode(): void {
    this.setDarkMode(!this._dark());
  }

  /**
   * Restore the persisted dark-mode choice. First visit (no stored value) falls back to
   * the OS `prefers-color-scheme` so dark-OS users land in dark without a flash-then-flip.
   */
  private readStoredDark(): boolean {
    try {
      const stored = localStorage.getItem(DARK_MODE_KEY);
      if (stored !== null) {
        return stored === '1';
      }
      return this.document.defaultView?.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
    } catch {
      // localStorage / matchMedia unavailable (private mode, non-browser) — default light.
      return false;
    }
  }

  private persistDark(dark: boolean): void {
    try {
      localStorage.setItem(DARK_MODE_KEY, dark ? '1' : '0');
    } catch {
      // Storage unavailable — non-fatal; the choice just won't survive a reload.
    }
  }
}
